/**
 * API utilities for OpenAI image generation using our backend
 */

// API base URL - change this based on your environment
const API_BASE_URL = "http://localhost:5001/api";

/**
 * Generate a modified image using OpenAI's image generation API via our backend
 * @param {File} originalImage - The original image file
 * @param {string} prompt - The customization prompt
 * @returns {Promise<string>} - URL of the generated image
 */
export const generateModifiedImage = async (originalImage, prompt) => {
  try {
    // Create a FormData object to send the image and prompt
    const formData = new FormData();
    formData.append("image", originalImage);
    formData.append("prompt", prompt);

    // Call our backend API
    const response = await fetch(`${API_BASE_URL}/generate-image`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to generate image");
    }

    const data = await response.json();

    // Return the full URL to the generated image directly from the server
    return data.imageUrl;
  } catch (error) {
    console.error("Error generating image:", error);
    throw error;
  }
};

/**
 * Save a generated image to the server
 * @param {string} imageUrl - URL of the image to save
 * @param {string} filename - Desired filename
 * @returns {Promise<string>} - URL of the saved image
 */
export const saveGeneratedImage = async (
  imageUrl,
  filename = "generated-image.png"
) => {
  try {
    // Call our backend API to save the image
    const response = await fetch(`${API_BASE_URL}/save-image`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        imageUrl,
        filename,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to save image");
    }

    const data = await response.json();
    return data.savedImageUrl;
  } catch (error) {
    console.error("Error saving image:", error);
    throw error;
  }
};
